import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';

import { useToggle } from 'react-use';

import Chip from '@mui/material/Chip';
import { makeStyles } from 'tss-react/mui';

import ArrowDropDown from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUp from '@mui/icons-material/ArrowDropUpRounded';
import CancelIcon from '@mui/icons-material/CancelRounded';
import { Box } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import QTip from 'components/QuickenControls/QTip';

const styles = (theme) => ({
  chip: {
    margin: 4,
    fontSize: 12,
    width: 102,
    justifyContent: 'flex-start',
    textOverflow: 'ellipsis',
    color: theme.palette.text.tertiary,
    backgroundColor: theme.palette.background.default,
    '&:hover span': {
      color: theme.palette.text.primary,
    },
  },
  adornedEndIcon: {
    color: theme.palette.action.active,
    marginRight: 12,
  },
  chipRoot: {
    overflow: 'hidden',
    width: 102,
    margin: '4px 4px',
    height: 32,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.layer1,
    fontSize: 12,
    justifyContent: 'space-between',
    position: 'relative',
    '& > span': {
      cursor: 'pointer',
      display: 'block',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      maxWidth: 'calc(100% - 10px)',
    },
    '& > svg': {
      position: 'absolute',
      // display: 'none',
      width: theme.spacing(2.25),
      height: theme.spacing(2.25),
      right: 0,
      color: theme.palette.icon.main,
    },
    '&:hover > svg': {
      // display: 'block',
    },
  },
});
const useStyles = makeStyles()(styles);

const ChipList = React.forwardRef((props, ref) => {
  const { chips, maxChips, onRemoveChip, expandable, zeroDiv, showChips, endAdornment, id, onBlur, onFocus } = props;
  const { classes } = useStyles();

  const [expanded, toggleExpanded] = useToggle(false);

  const expandableChip = useMemo(
    () =>
      chips.length > maxChips && !expanded ? (
        <Chip
          clickable
          label={!expanded ? `+${chips.length - maxChips} more` : 'collapse'}
          onClick={toggleExpanded}
          className={classes.chip}
        />
      ) : null,
    [chips, maxChips, expanded, classes.chip, toggleExpanded],
  );

  const chipList = useMemo(
    () => (expandableChip && !expanded ? chips.slice(0, maxChips) : chips),
    [expandableChip, expanded, chips, maxChips],
  );

  const handleRemoveChip = useCallback(
    (item) => (_e) => {
      onRemoveChip(item);
      if (chips.length > maxChips) {
        toggleExpanded(false);
      }
    },
    [onRemoveChip, chips, maxChips, toggleExpanded],
  );

  return (
    <Box display={'flex'} width={'100%'} alignItems={'center'} id={id}>
      <input
        ref={ref}
        style={{
          opacity: 0,
          width: 0,
          height: 0,
          padding: 0,
          border: 0,
          marginLeft: -14,
          marginRight: 10,
          outline: 'none',
        }}
        onFocus={onFocus}
        onBlur={onBlur}
      />

      <Box display={'flex'} width={'100%'} flexWrap={'wrap'}>
        {showChips &&
          chipList.map((item, index) => {
            const chip = (
              <Chip
                key={`chip-list-item-${item.id}`}
                onDelete={handleRemoveChip(item)}
                deleteIcon={<CancelIcon id={`delete-search-item-button-${index}`} />}
                label={item.name?.length > 0 ? item.name : 'error'}
                classes={{ root: classes.chipRoot }}
              />
            );
            return item.name.length > 11 ? (
              <QTip key={`qtip-chip-list-item-${item.id}`} title={item.name}>
                {chip}
              </QTip>
            ) : (
              chip
            );
          })}
        {((chips.length === 0 && zeroDiv) || !showChips) && <div style={{ height: 23 }} />}
        {expandable && expandableChip}
      </Box>

      {endAdornment && (
        <InputAdornment className={classes.adornedEndIcon} disablePointerEvents position='end'>
          {!showChips ? <ArrowDropUp /> : <ArrowDropDown />}
        </InputAdornment>
      )}
    </Box>
  );
});

ChipList.displayName = 'ChipList';

ChipList.defaultProps = {
  expandable: true,
  showChips: true,
  endAdornment: false,
};
ChipList.propTypes = {
  chips: PropTypes.array,
  expandable: PropTypes.bool,
  maxChips: PropTypes.number,
  onRemoveChip: PropTypes.func,
  zeroDiv: PropTypes.bool,
  showChips: PropTypes.bool,
  endAdornment: PropTypes.bool,
  id: PropTypes.string,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
};

export default ChipList;
